/**
 * IP Whitelist Tab Component
 * Manages IP address whitelisting settings
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Globe, Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const IPWhitelistTab = ({ settings, setSettings }) => {
  const [newIP, setNewIP] = useState("");

  const addIP = () => {
    const ip = newIP.trim();
    if (!ip) return;
    
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(ip)) {
      toast.error("Please enter a valid IP address");
      return;
    }
    
    if (settings.whitelistedIPs.includes(ip)) {
      toast.error("This IP is already whitelisted");
      return;
    }
    
    setSettings({
      ...settings,
      whitelistedIPs: [...settings.whitelistedIPs, ip]
    });
    setNewIP("");
    toast.success(`IP ${ip} added to whitelist`);
  };

  const removeIP = (ip) => {
    setSettings({
      ...settings,
      whitelistedIPs: settings.whitelistedIPs.filter(i => i !== ip)
    });
    toast.success(`IP ${ip} removed from whitelist`);
  };

  return (
    <Card className="border-amber-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Globe className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-xl text-amber-800">IP Whitelist</CardTitle>
              <CardDescription>Only Permit Access to these specific IP addresses</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-amber-700">Enabled</Label>
            <Switch
              checked={settings.ipWhitelistEnabled}
              onCheckedChange={(checked) => setSettings({...settings, ipWhitelistEnabled: checked})}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {settings.ipWhitelistEnabled && (
          <>
            <div className="flex gap-2">
              <Input
                value={newIP}
                onChange={(e) => setNewIP(e.target.value)}
                placeholder="Enter IP address (e.g., 192.168.1.1)"
                className="border-amber-200"
                onKeyPress={(e) => e.key === 'Enter' && addIP()}
              />
              <Button onClick={addIP} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            
            <div className="space-y-2">
              {settings.whitelistedIPs.length === 0 ? (
                <div className="text-center py-4 text-amber-600 bg-amber-50 rounded-lg">
                  <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
                  <p>No IPs whitelisted. Add your current IP to prevent lockout.</p>
                </div>
              ) : (
                settings.whitelistedIPs.map((ip, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono">{ip}</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeIP(ip)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
        
        {!settings.ipWhitelistEnabled && (
          <p className="text-gray-500 text-sm">
            Enable IP whitelisting to restrict access to your system from specific IP addresses only.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default IPWhitelistTab;
